import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const offerings = [
  {
    id: 'custom-dev',
    icon: 'ri-code-box-line',
    title: '工具软件定制开发',
    subtitle: 'Custom Software Development',
    tag: '直接交付',
    tagColor: '#F97316',
    problem: '你有明确的业务需求，但传统外包开发周期长、成本高、交付后改不动。',
    whyNot: '传统外包依赖人力堆积，沟通成本高，交付后维护困难，无法快速响应需求变化。你拿到的往往是一个黑盒。',
    howWeDo: '我们用 AI 协作方式直接参与开发。更短的交付周期、更低的成本、更高的代码质量。全程透明，你每天都能看到进度，随时介入。',
    deliverables: ['可交付的软件产品', '完整的技术文档', '源代码所有权', '部署和运维支持', '上线后持续迭代'],
    coop: '以项目制为主，明确交付目标和时间节点。支持驻场或远程协作。',
    color: '#F97316',
    scenarios: ['内部管理工具', '业务自动化系统', '数据看板与报表', '客户端 / 后台系统'],
  },
  {
    id: 'legacy',
    icon: 'ri-refresh-line',
    title: '老系统升级改造',
    subtitle: 'Legacy System Modernization',
    tag: '深度改造',
    tagColor: '#2563EB',
    problem: '老系统积累了大量技术债，想改但改不动，想引入 AI 但 AI 根本无法理解项目。',
    whyNot: '老系统改造风险高，传统方式需要大量人力，而且很容易引入新的问题。大多数团队选择忍着，直到系统彻底撑不住。',
    howWeDo: '先重构工程环境，让 AI 能够理解项目。再用 AI 协作方式逐步改造，降低风险，提高效率。每个阶段都有明确的交付物和验收标准。',
    deliverables: ['工程环境重构方案', '分阶段改造计划', '改造后的系统', 'AI 可理解的代码结构', '技术债清单与处理记录'],
    coop: '通常以阶段性陪跑形式进行，分阶段交付，降低风险。',
    color: '#2563EB',
    scenarios: ['Java / PHP 老系统现代化', '单体架构拆分', '数据库迁移', '前端技术栈升级'],
  },
  {
    id: 'agent',
    icon: 'ri-robot-line',
    title: 'Agent 平台定制',
    subtitle: 'Agent Platform Customization',
    tag: 'AI 原生',
    tagColor: '#0F172A',
    problem: '你想让 AI 真正参与业务流程，而不只是一个聊天窗口。但通用 Agent 平台无法满足你的特定需求。',
    whyNot: '通用 Agent 平台是为大众设计的，无法深度理解你的业务逻辑、数据结构和工作流。套模板的结果是用起来别扭，最终弃用。',
    howWeDo: '深入理解你的业务流程，设计专属 Agent 架构，构建与现有系统深度集成的 AI Agent 平台。不是套壳，是真正的业务 AI 化。',
    deliverables: ['Agent 架构设计文档', '定制化 Agent 平台', '与现有系统的集成', '使用培训与文档', '持续优化支持'],
    coop: '以项目制为主，可延伸为长期技术支持合作。',
    color: '#0F172A',
    scenarios: ['智能客服 / 销售助手', '内部知识库 Agent', '业务流程自动化', '数据分析 Agent'],
  },
];

const whyUs = [
  {
    icon: 'ri-flashlight-line',
    title: '交付速度快 2–3 倍',
    desc: 'AI 协作方式让我们的开发效率远超传统团队，同样的需求，我们用更短的时间完成。',
  },
  {
    icon: 'ri-eye-line',
    title: '全程透明可见',
    desc: '不是黑盒外包。你每天都能看到进度，代码在你的仓库里，随时可以介入。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '深度参与，不是甩手',
    desc: '我们驻场或高频远程协作，和你的团队一起工作，而不是拿了需求就消失。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '源代码完全归你',
    desc: '交付物包含完整源代码和文档，没有任何锁定。你的系统，你说了算。',
  },
];

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function SoftwareServicesPage() {
  const [active, setActive] = useState(offerings[0].id);
  const current = offerings.find((o) => o.id === active) ?? offerings[0];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '\'Noto Sans SC\', Inter, sans-serif' }}>
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/bg-tech-dark.svg"
            alt=""
            className="w-full h-full object-cover object-top opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-[#0F172A]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#F97316]/40 text-[#F97316] text-xs mb-6">
              <i className="ri-code-s-slash-line" />
              Software Services
            </div>
            <h1
              className="text-4xl md:text-6xl font-bold text-white leading-tight mb-6"
              style={{ letterSpacing: '-0.02em' }}
            >
              我们直接为你<br />
              <span className="text-[#F97316]">交付软件</span>。
            </h1>
            <p className="text-lg text-white/55 leading-relaxed mb-10 max-w-xl">
              不是给你一套方法论，不是帮你培训团队。<br />
              你有需求，我们用 AI 协作方式直接做出来。
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
              >
                告诉我们你的需求
              </Link>
              <Link
                to="/cases"
                className="px-7 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 transition-colors"
              >
                查看交付案例
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Statement */}
      <section className="py-16 bg-[#F8FAFC] border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-100 rounded-2xl overflow-hidden">
              {[
                { icon: 'ri-close-circle-line', label: '不是', value: '传统外包', color: '#94A3B8' },
                { icon: 'ri-close-circle-line', label: '不是', value: '课堂培训', color: '#94A3B8' },
                { icon: 'ri-check-double-line', label: '而是', value: 'AI 驱动的软件交付', color: '#F97316' },
              ].map((item) => (
                <div key={item.value} className="bg-white px-8 py-7 flex items-center gap-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                    <i className={`${item.icon} text-base`} style={{ color: item.color }} />
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8] mb-0.5">{item.label}</p>
                    <p className="text-sm font-bold text-[#0F172A]">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Three Offerings */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="mb-12">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>
                What We Build
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ letterSpacing: '-0.02em' }}>
                三类软件服务
              </h2>
            </div>
            <div className="flex flex-wrap gap-3 mb-10">
              {offerings.map((o) => (
                <button
                  key={o.id}
                  onClick={() => setActive(o.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    active === o.id
                      ? 'bg-[#0F172A] text-white'
                      : 'bg-[#F8FAFC] text-[#64748B] border border-slate-100 hover:border-slate-200'
                  }`}
                >
                  <i className={`${o.icon} text-sm`} />
                  {o.title}
                </button>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              {/* Left */}
              <div className="lg:col-span-2">
                <div className="bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-6">
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-2xl"
                      style={{ backgroundColor: `${current.color}15` }}
                    >
                      <i className={`${current.icon} text-2xl`} style={{ color: current.color }} />
                    </div>
                    <span
                      className="text-xs font-semibold px-3 py-1 rounded-full"
                      style={{ backgroundColor: `${current.tagColor}15`, color: current.tagColor }}
                    >
                      {current.tag}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-1">{current.title}</h3>
                  <p className="text-xs text-[#64748B] mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>{current.subtitle}</p>

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>适用场景</p>
                    <div className="flex flex-wrap gap-2">
                      {current.scenarios.map((s) => (
                        <span key={s} className="text-xs px-3 py-1 rounded-full bg-white border border-slate-100 text-[#64748B]">{s}</span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>交付物</p>
                    <ul className="flex flex-col gap-2">
                      {current.deliverables.map((d) => (
                        <li key={d} className="flex items-center gap-2.5">
                          <i className="ri-check-line text-sm" style={{ color: current.color }} />
                          <span className="text-sm text-[#0F172A]">{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto pt-5 border-t border-slate-100">
                    <p className="text-xs uppercase tracking-widest text-[#64748B] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>合作方式</p>
                    <p className="text-sm text-[#64748B] leading-relaxed">{current.coop}</p>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="lg:col-span-3 flex flex-col gap-5">
                {[
                  { label: '你遇到的问题', icon: 'ri-question-line', content: current.problem, accent: '#F97316' },
                  { label: '为什么传统方式解决不了', icon: 'ri-close-circle-line', content: current.whyNot, accent: '#64748B' },
                  { label: '我们怎么做', icon: 'ri-arrow-right-circle-line', content: current.howWeDo, accent: '#2563EB' },
                ].map((block) => (
                  <div key={block.label} className="bg-white rounded-2xl p-7 border border-slate-100">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${block.accent}15` }}>
                        <i className={`${block.icon} text-sm`} style={{ color: block.accent }} />
                      </div>
                      <span className="text-sm font-semibold text-[#0F172A]">{block.label}</span>
                    </div>
                    <p className="text-sm text-[#64748B] leading-relaxed">{block.content}</p>
                  </div>
                ))}
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors"
                >
                  预约交流，聊聊你的需求
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why Us */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>Why Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ letterSpacing: '-0.02em' }}>
                和传统外包有什么不同
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyUs.map((item, idx) => (
              <FadeIn key={item.title} delay={idx * 80}>
                <div className="bg-white rounded-2xl p-7 border border-slate-100 h-full">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#F97316]/10 mb-5">
                    <i className={`${item.icon} text-xl text-[#F97316]`} />
                  </div>
                  <h3 className="text-sm font-bold text-[#0F172A] mb-2">{item.title}</h3>
                  <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-widest text-[#64748B] mb-3 block" style={{ fontFamily: 'Inter, sans-serif' }}>Process</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A]" style={{ letterSpacing: '-0.02em' }}>
                从需求到交付
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: '需求诊断', desc: '深入了解你的业务目标、技术现状和团队情况，明确交付范围和验收标准。', icon: 'ri-search-eye-line' },
              { step: '02', title: '方案设计', desc: '给出技术方案、时间节点和报价。透明、可预期，没有隐性成本。', icon: 'ri-draft-line' },
              { step: '03', title: '共建交付', desc: '驻场或高频远程协作，每天可见进度。你随时可以介入、调整方向。', icon: 'ri-team-line' },
              { step: '04', title: '上线运维', desc: '系统上线后持续跟踪，根据实际使用反馈迭代优化，确保真正产生价值。', icon: 'ri-rocket-line' },
            ].map((item, idx) => (
              <FadeIn key={item.step} delay={idx * 80}>
                <div className="relative">
                  <div className="bg-[#F8FAFC] rounded-2xl p-7 border border-slate-100 h-full">
                    <div className="flex items-center gap-3 mb-5">
                      <span className="text-3xl font-bold text-slate-100" style={{ fontFamily: 'Inter, sans-serif' }}>{item.step}</span>
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#0F172A]/5">
                        <i className={`${item.icon} text-base text-[#0F172A]`} />
                      </div>
                    </div>
                    <h3 className="text-sm font-bold text-[#0F172A] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#64748B] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ letterSpacing: '-0.01em' }}>
                  有软件需求？<br />我们来做。
                </h2>
                <p className="text-base text-white/50 leading-relaxed">
                  不管是从零开始的新系统，还是改不动的老系统，还是想让 AI 真正参与业务流程——告诉我们你的情况，我们来判断能不能帮到你。
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
                <Link
                  to="/contact"
                  className="px-7 py-3.5 rounded-full bg-[#F97316] text-white text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-[#ea6c0a] transition-colors text-center"
                >
                  预约免费需求沟通
                </Link>
                <Link
                  to="/cases"
                  className="px-7 py-3.5 rounded-full border border-white/20 text-white text-sm font-medium whitespace-nowrap cursor-pointer hover:border-white/40 transition-colors text-center"
                >
                  查看交付案例
                </Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
